import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Input,
  Label
} from 'reactstrap'
import SalesDealsApi from 'api/admins/sales-deals-api'

const UploadTransferReceiptCard = ({
  deal,
  payment,
  paymentIndex,
  totalPayments,
  fetchDeal,
  canUpload = true,
  lockReason = null,
  autoCreatePendingPayment = false,
  onPaymentCreated = () => {},
}) => {
  const [receiptFiles, setReceiptFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState(null)
  const [brokenExistingReceiptMap, setBrokenExistingReceiptMap] = useState({})

  const bankRef = useRef(null)
  const bankBranchRef = useRef(null)
  const accountNameRef = useRef(null)
  const accountNumberRef = useRef(null)

  /* ==============================
     EXISTING RECEIPTS (FROM SERVER)
  ============================== */

  const existingPayment =
    Array.isArray(deal?.payments)
      ? deal.payments.find(p => p?.id === payment?.id)
      : null

  const existingReceipts =
    Array.isArray(existingPayment?.all_receipt_files) && existingPayment.all_receipt_files.length > 0
      ? existingPayment.all_receipt_files
      : (existingPayment?.all_receipt_urls || []).map((url) => ({
          url,
          filename: '',
          content_type: ''
        }))

// Pada mode pembayaran berikutnya, jangan tampilkan receipt payment lama
// di card upload baru agar tidak membingungkan SA.
const visibleExistingReceipts = autoCreatePendingPayment ? [] : existingReceipts
const hasExistingReceipts = visibleExistingReceipts.length > 0

  const isPdfReceipt = (file) => {
    const contentType = (file?.content_type || '').toLowerCase()
    const filename = (file?.filename || file?.url || '').toLowerCase().split('?')[0]

    return contentType === 'application/pdf' || filename.endsWith('.pdf')
  }

  useEffect(() => {
    setBrokenExistingReceiptMap({})
  }, [payment?.id, deal?.id])

  /* ==============================
     HANDLE FILE PICK
  ============================== */

const onPick = (e) => {
  const files = Array.from(e.target.files || [])

  /* ==============================
    VALIDATE MAX FILE COUNT
  ============================== */
  const totalFiles = files.length
  if (totalFiles > MAX_FILES) {
    setResult({
      color: 'danger',
      message: `Maximum ${MAX_FILES} files allowed`
    })
    return
  }

  /* ==============================
    VALIDATE FILE SIZE
  ============================== */
  const oversizedFile = files.find(file => file.size > MAX_SIZE)
  if (oversizedFile) {
    setResult({
      color: 'danger',
      message: `File "${oversizedFile.name}" exceeds 2MB limit`
    })
    return
  }

  setReceiptFiles(files)
  setResult(null)

  e.target.value = null
}

  /* ==============================
   FILE VALIDATION CONFIG
  ============================== */

const MAX_FILES = 10
const MAX_SIZE = 2 * 1024 * 1024 // 2MB

  /* ==============================
     REMOVE FILE BEFORE UPLOAD
  ============================== */

  const removeLocalFile = (index) => {
    const updated = [...receiptFiles]
    updated.splice(index, 1)
    setReceiptFiles(updated)
  }

  /* ==============================
     SUBMIT
  ============================== */

  const submit = async () => {
    if (!receiptFiles.length) return

    setUploading(true)
    setResult(null)

    try {
      const formData = new FormData()

      receiptFiles.forEach(file => {
        formData.append('all_receipt_urls[]', file)
      })

      formData.append('bank', bankRef.current?.value || '')
      formData.append('bank_branch', bankBranchRef.current?.value || '')
      formData.append('bank_account_name', accountNameRef.current?.value || '')
      formData.append('bank_account_number', accountNumberRef.current?.value || '')

const res = await SalesDealsApi.uploadTransferReceipt({
  dealId: deal.id,
  // Jika payment yang dipilih sudah CF, jangan kirim ID payment lama.
  // Backend akan membuat pending payment otomatis.
  paymentId: autoCreatePendingPayment ? null : payment?.id,
  formData,
})

      setResult({
        color: 'success',
        message: res?.data?.message || 'Receipt(s) uploaded successfully'
      })

setReceiptFiles([])
await fetchDeal()

const createdPaymentId = res?.data?.payment_id
if (createdPaymentId) onPaymentCreated(createdPaymentId)
    } catch (err) {
      setResult({
        color: 'danger',
        message: err?.response?.data?.message || err.message
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <Card className="mb-3">
      <CardBody>
<CardTitle tag="h5">
  {autoCreatePendingPayment
    ? 'Upload Bukti Pembayaran Berikutnya'
    : 'Upload Bukti Pelunasan (bisa lebih dari 1 file, max 5)'}
  {!autoCreatePendingPayment && paymentIndex ? (
    <span className="text-muted" style={{ fontSize: 12 }}>
      {` — Payment #${paymentIndex}${totalPayments ? `/${totalPayments}` : ''}`}
    </span>
  ) : null}
</CardTitle>

{autoCreatePendingPayment && (
  <Alert color="secondary">
    Cukup upload bukti pembayaran. Nominal akan divalidasi dan dikonfirmasi oleh Finance.
  </Alert>
)}

        {/* ==============================
            SHOW EXISTING RECEIPTS
        ============================== */}
        {hasExistingReceipts && (
          <div className="mb-4">
            <Label style={{ fontWeight: 600 }}>Uploaded Pelunasan Proof(s)</Label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {visibleExistingReceipts.map((file, i) => {
                const url = file?.url || ''
                const isPdf = isPdfReceipt(file)
                const isBroken = !!brokenExistingReceiptMap[i]

                return (
                  <div key={i}>
                    {isPdf ? (
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        View Receipt #{i + 1} (PDF)
                      </a>
                    ) : !isBroken ? (
                      <img
                        src={url}
                        alt={`receipt-${i}`}
                        onError={() => {
                          setBrokenExistingReceiptMap(prev => ({ ...prev, [i]: true }))
                        }}
                        style={{
                          width: '100%',
                          maxHeight: 300,
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          borderRadius: 6
                        }}
                      />
                    ) : (
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        Open Receipt #{i + 1}
                      </a>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {!canUpload && (
          <Alert color="secondary" className="mb-4">
            Read-only view.
            {lockReason ? ` ${lockReason}` : ''}
            {!hasExistingReceipts ? ' No receipt uploaded for this payment.' : ''}
          </Alert>
        )}

        {canUpload && (
          <>
            {/* ==============================
                FILE INPUT
            ============================== */}
            <FormGroup>
              <Label>Upload New Receipt(s)</Label>
                <Input
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={onPick}
                />

              {/* ==============================
                  FILE UPLOAD HINT
              ============================== */}
              <small className="text-muted">
                Upload bisa lebih dari 1 file dengan format (JPG/JPEG/PNG/PDF) Max 2MB.
              </small>
            </FormGroup>

            {/* ==============================
                LOCAL PREVIEW BEFORE UPLOAD
            ============================== */}
            {receiptFiles.length > 0 && (
              <div className="mb-3">
                <Label style={{ fontWeight: 600 }}>Preview (Before Upload)</Label>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {receiptFiles.map((file, idx) => {
                    const previewUrl = URL.createObjectURL(file)
                    const isPdf = file.type.includes('pdf')

                    return (
                      <div
                        key={idx}
                        style={{
                          border: '1px solid #ddd',
                          borderRadius: 6,
                          padding: 10,
                          position: 'relative'
                        }}
                      >
                        <Button
                          size="sm"
                          color="danger"
                          style={{ position: 'absolute', top: 8, right: 8 }}
                          onClick={() => removeLocalFile(idx)}
                        >
                          ✕
                        </Button>

                        {isPdf ? (
                          <>
                            <div>📄 PDF File</div>
                            <div style={{ fontSize: 12 }}>{file.name}</div>
                          </>
                        ) : (
                          <>
                            <img
                              src={previewUrl}
                              alt="preview"
                              style={{
                                width: '100%',
                                maxHeight: 300,
                                objectFit: 'contain'
                              }}
                            />
                            <div style={{ fontSize: 12 }}>{file.name}</div>
                          </>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <hr />

            <Button
              block
              color="primary"
              disabled={!receiptFiles.length || uploading}
              onClick={submit}
            >
              {uploading ? 'Uploading...' : 'Upload Receipt(s)'}
            </Button>
          </>
        )}

        {result && (
          <Alert color={result.color} className="mt-3">
            {result.message}
          </Alert>
        )}
      </CardBody>
    </Card>
  )
}

export default UploadTransferReceiptCard
